import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PackService } from '../../services/pack.service';
import { CreatePackDTO, Pack } from 'src/app/shared/model/pack';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ButtonModule } from 'primeng/button';
import { ConnectedUserProfileComponent } from '../connected-user-profile/connected-user-profile.component';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { InputNumber } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CompanySize } from 'src/app/user/model/user';
import { TabsModule } from 'primeng/tabs';

@Component({
  selector: 'app-edit-pack',
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    ConnectedUserProfileComponent,
    InputTextModule,
    TextareaModule,
    InputNumber,
    CheckboxModule,
    ConfirmDialogModule,
    ToastModule,
    TabsModule,
  ],
  templateUrl: './edit-pack.component.html',
})
export class EditPackComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activeRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private packService = inject(PackService);
  private notificationService = inject(NotificationService);
  private confirmationService = inject(ConfirmationService);

  editMode = false;
  useUniqueConfig = false;

  pack?: Pack;

  packForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    description: [''],
    exportPDF: [false, Validators.required],
    exportExcel: [false, Validators.required],
    clientFilter: [false, Validators.required],
    comparisonGraph: [false, Validators.required],
    useApi: [false, Validators.required],
    useGoogleApi: [false, Validators.required],
    apiCallsPerMonth: [0, [Validators.required, Validators.min(0)]],
    packSizeConfigForm: this.fb.group({
      micro: this.createSizeConfigGroup(),
      small: this.createSizeConfigGroup(),
      medium: this.createSizeConfigGroup(),
      large: this.createSizeConfigGroup(),
    }),
    uniqueSizeConfigForm: this.createSizeConfigGroup(),
  });

  companySizes = Object.values(CompanySize);

  ngOnInit() {
    this.setMode();
    this.setupUseApiDependency();
    this.initializeForm();
  }

  /**
   * Google API access is meaningless without API access enabled, so it
   * tracks useApi: unchecked/disabled whenever useApi is off, re-enabled
   * (keeping its own value) whenever useApi is on. Runs once immediately
   * (covers the create-pack default and is re-applied after initializeForm's
   * patchValue resolves, so load order can't leave the two controls out of
   * sync) and again on every future toggle of useApi.
   */
  private setupUseApiDependency() {
    const useApiControl = this.packForm.controls.useApi;
    useApiControl.valueChanges.subscribe((useApi) =>
      this.syncUseGoogleApiAvailability(useApi),
    );
    this.syncUseGoogleApiAvailability(useApiControl.value);
  }

  private syncUseGoogleApiAvailability(useApi: boolean) {
    const useGoogleApiControl = this.packForm.controls.useGoogleApi;
    if (useApi) {
      useGoogleApiControl.enable({ emitEvent: false });
    } else {
      useGoogleApiControl.setValue(false, { emitEvent: false });
      useGoogleApiControl.disable({ emitEvent: false });
    }
  }
  private createSizeConfigGroup() {
    return this.fb.nonNullable.group({
      price: [0, [Validators.required, Validators.min(0)]],
      userLimit: [1, [Validators.required, Validators.min(1)]],
      tripLimit: [1, [Validators.required, Validators.min(1)]],
      extraUserLimit: [null as number | null],
      bulkUpload: [0, [Validators.required, Validators.min(0)]],
    });
  }

  setMode() {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.editMode = true;
    }
  }

  initializeForm() {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.packService.getPackById(id).subscribe({
        next: (pack: Pack) => {
          this.pack = pack;
          const data: CreatePackDTO = pack;
          this.packForm.patchValue(data);
          // Authoritative re-sync: patchValue sets useApi and useGoogleApi
          // independently, so whichever order it applies them in, this makes
          // sure the disabled/false state matches the loaded pack's useApi.
          this.syncUseGoogleApiAvailability(pack.useApi);
          if (pack.packSizeConfig?.length) {
            this.useUniqueConfig = pack.packSizeConfig.length === 1;

            if (this.useUniqueConfig) {
              const config = pack.packSizeConfig[0];
              this.packForm.controls.uniqueSizeConfigForm.patchValue({
                price: config.price,
                userLimit: config.userLimit,
                tripLimit: config.tripLimit,
                extraUserLimit: config.extraUserLimit,
                bulkUpload: config.bulkUpload,
              });
            } else {
              pack.packSizeConfig.forEach((config) => {
                if (!config.companySize) return;
                const sizeGroup = this.packForm.get(
                  `packSizeConfigForm.${config.companySize}`,
                );
                sizeGroup?.patchValue({
                  price: config.price,
                  userLimit: config.userLimit,
                  tripLimit: config.tripLimit,
                  extraUserLimit: config.extraUserLimit,
                  bulkUpload: config.bulkUpload,
                });
              });
            }
          }
        },

        error: (err) => console.error('Error fetching pack:', err),
      });
    }
  }

  async submitForm() {
    if (this.packForm.invalid) {
      this.packForm.markAllAsTouched();
      return;
    }

    const value = this.packForm.getRawValue();
    const packSizeConfig = this.useUniqueConfig
      ? [{ companySize: null, ...value.uniqueSizeConfigForm }]
      : this.companySizes.map((size) => ({
          companySize: size,
          ...value.packSizeConfigForm[size],
        }));
    const packDto: CreatePackDTO = {
      ...value,
      packSizeConfig,
    };

    if (this.editMode && this.pack) {
      this.packService.updatePack(packDto, this.pack.id).subscribe({
        next: (pack) => {
          this.pack = pack;
          this.notificationService.success('Information successfully updated');
          this.backToPack();
        },
        error: (err) => {
          console.error('Error updating pack:', err),
            this.notificationService.error('error while updating informations');
        },
      });
    } else {
      this.packService.createPack(packDto).subscribe({
        next: (pack) => {
          this.pack = pack;
          this.notificationService.success('pack successfully created');
          this.backToPack();
        },
        error: (err) => {
          console.error('Error creating pack', err);
          this.notificationService.error('Error while creating pack');
        },
      });
    }
  }
  confirmDelete() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this pack?',
      header: 'delete confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deletePack(),
    });
  }
  deletePack() {
    if (this.pack) {
      this.packService.deletePack(this.pack.id).subscribe({
        next: () => {
          this.notificationService.success('pack successfully deleted');
          this.backToPack();
        },
        error: (err) => {
          console.error('error while deleting pack', err);
          this.notificationService.error(err.error.message);
        },
      });
    }
  }
  backToPack() {
    this.router.navigate(['/admin/pack']);
  }
}
